

import { useParams } from "react-router-dom"
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { apiFetch } from '@/lib/api'
import ChatBox from '@/components/ChatBox'
import { FiPlay, FiPlus, FiSend, FiTrash2, FiPause, FiLink, FiCopy, FiMessageCircle, FiX, FiPlusCircle, FiUser, FiUserPlus, FiUserMinus, FiCheck, FiMoreHorizontal, FiEdit, FiChevronDown, FiChevronUp, FiDownload } from 'react-icons/fi'
import { PiWarningDiamondFill } from "react-icons/pi";
import Button from '@/components/ui/Button'
import TextArea from '@/components/ui/TextArea'

import { LuLoaderCircle, LuCheck } from "react-icons/lu"
import { RiAiGenerateText } from "react-icons/ri"
import { useShow } from '@/contexts/ShowContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import { CgInsertBefore, CgInsertAfter } from "react-icons/cg";
import { useModal } from '@/contexts/ModalContext'
import ConfirmModal from '@/components/modals/ConfirmModal'
import { UserRole, useUser, UserOwns } from '@/contexts/UserContext'
import languages from '@/constants/languages'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel} from "@/components/ui/shadcn/select"
import Loader from '@/components/loader'
import { Switch } from "@/components/ui/shadcn/switch"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/shadcn/dropdown-menu"
import { Button as ShadButton } from "@/components/ui/shadcn/button"
import { SlOptions } from "react-icons/sl"
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import { Input } from "@/components/ui/shadcn/input"
import VoiceSettingsModal from '@/components/modals/VoiceSettingsModal'
import Chat from "@/components/GenAgent"
import { BsChatRightTextFill } from "react-icons/bs";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/shadcn/resizable"
import CommentMentionsInput from '@/components/comments/CommentMentionsInput'
import CommentGroup from '@/components/comments/CommentGroup'
import { LuBrainCircuit } from "react-icons/lu";
import { FaArrowUp } from "react-icons/fa";


dayjs.extend(duration)
dayjs.extend(utc)

const ScriptElement = forwardRef(function ScriptElement({data, index=0, onAdd, onDelete, onComment, highlighted = false, saving}, ref) {
    const [element, setElement] = useState(data)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [charVisible, setCharVisible] = useState(element?.character ? true : false)
    const [adding, setAdding] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [updating, setUpdating] = useState(false)
    const [updatingStatus, setUpdatingStatus] = useState('')
    const {show} = useShow()
    const {fetchEpisode} = useEpisode()
    const {user} = useUser()
    const {runTaskAndPoll, taskStatus, taskState} = useKagamiBridge()
    const [saveTimeout, setSaveTimeout] = useState(null)
    const scriptRef = useRef(null)

    const getData = async () => {
        try{
            const response = await apiFetch(`/api/shows/${element.show}/episodes/${element.episode}/segments/${element.segment}/scenes/${element.scene}/script/${element._id}`)
            if(!response.ok){
                throw new Error('Failed to fetch script element')
            }
            const data = await response.json()
            setElement(data)
        }
        catch(error){
            console.error('Error fetching script element:', error)
        }
    }

    const updateElement = async (data, debounce = true) => {
        setElement({...element, ...data})

        if(debounce){
            debouncedSave(data)
        }else{
            handleSave(data)
        }
    }

    const handleAddElement = async () => {
        try{
            setAdding(true)
            await onAdd?.()
        } catch (error) {
            console.error('Error:', error)
        }
        finally{
            setAdding(false)
        }
    }

    const handleDeleteElement = async () => {
        try{
            setDeleting(true)
            const response = await apiFetch(`/api/shows/${element.show}/episodes/${element.episode}/segments/${element.segment}/scenes/${element.scene}/script/${element._id}`, {
                method: 'DELETE'
            })
            if(!response.ok){
                throw new Error('Failed to delete script element')
            }
            const deletedElement = await response.json()
            await onDelete?.(deletedElement)
        }
        catch(error){
            console.error('Error:', error)
        }
        finally{
            setDeleting(false)
        }
    }

    const handleCharVisible = (visible) => {
        setCharVisible(visible)
        if(visible){
            if(show.characters.length){
                updateElement({character: show.characters[0]}, false)
            }
        }
        else{
            updateElement({character: null}, false)
        }
    }

    const handleEditScript = async(script, prompt) => {
        try{
            setUpdating(true)
            await runTaskAndPoll({
                function: 'edit-script-grok',
                showId: show?._id,
                payload: {
                    scriptId: script._id,
                    prompt:  prompt
                }
            })
            
            await getData()
        }catch(error){
            console.log(error)
        }finally{
            setUpdating(false)
        }
    }

    const handleAddComment = async() => {
        await onComment?.(element)
    }

    const debouncedSave = (data) =>{
        if(saveTimeout) clearTimeout(saveTimeout)
        setSaveTimeout(setTimeout(() => handleSave(data), 2000))
    }

    const handleSave = async (data) => {
        try {
            saving?.(true)
            const response = await apiFetch(`/api/shows/${element.show}/episodes/${element.episode}/segments/${element.segment}/scenes/${element.scene}/script/${element._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            })
            
            if (!response.ok) {
                throw new Error('Failed to save script')
            }
        } catch (error) {
            console.error('Error saving script:', error)
        } 
        finally{
            saving?.(false)
        }
    }

    const scrollIntoView = () => {
        scriptRef?.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }

    useImperativeHandle(ref, () => ({
        scrollIntoView : scrollIntoView
    }))

    return (
        <div className='flex w-[calc(100%+180px)] ml-[-150px] mr-[-30px] group/script' ref={scriptRef}>
            <div className='w-[150px] flex justify-end items-center pb-6 pr-1 py-1'>
                <UserRole role='editor'>
                    <div className='gap-1 px-1 flex flex-col opacity-0 group-hover/script:opacity-100'>
                        {element.character ? <Button type='secondary' size='xs' icon={<FiUserMinus className='w-3 h-3' />} onClick={() => handleCharVisible(false)} /> : <Button type='secondary' size='xs' icon={<FiUserPlus className='w-4 h-4' />} onClick={() => handleCharVisible(true)} /> }
                        <DropdownMenu>
                            <DropdownMenuTrigger>
                                <div className='bg-secondary text-secondary-foreground border shadow hover:bg-secondary/80 p-1 text-[10px] rounded-sm'>
                                    <RiAiGenerateText className='w-4 h-4' />
                                </div>
                                
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Rephrase the script, but keep it roughly at the same length and keep the same meaning')}>Rephrase</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Make the script text longer, but keep the same meaning')}>Longer</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Make the script text shorter, but keep the same meaning')}>Shorten</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Summarize the script text, but keep the same meaning')}>Summarize</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Make the script text more dramatic')}>Dramatic</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Make the script text more emotional')}>Emotional</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Make the script text more exciting')}>Exciting</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditScript(element, 'Make the script text more engaging')}>Engaging</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <Button type='danger-ghost' size='xs' icon={<FiTrash2 className='w-3 h-3' />} onClick={handleDeleteElement} />                       
                    </div>
                </UserRole>
            </div>
            <div className={`flex flex-col gap-1 flex-1 items-center pb-6 pt-2 relative  ${highlighted ? 'bg-secondary/30' : ''}  ${element?.character ? 'items-center' : ''}  ${deleting || updating ? 'opacity-30 animate-pulse pointer-events-none' : ''}`}>
                {charVisible && 
                    <div className='relative w-full flex gap-1 items-center justify-center group/char'>
                        <Select value={element?.character?._id} onValueChange={(e) => updateElement({character:  show.characters.find(ch=>ch._id === e)}, false)}>
                            <SelectTrigger className={`font-mono py-1 px-2 text-[12px] justify-center bg-transparent outline-none items-center border-transparent hover:border-secondary-foreground/10 rounded-sm focus:outline-none focus:ring-0 outline-hidden ${user.role < 20 ? 'pointer-events-none' : ''}`}>
                                <SelectValue placeholder="Character" />
                            </SelectTrigger>
                            <SelectContent className='bg-background'>
                                <SelectGroup>
                                    {show.characters.map(char => (
                                        <SelectItem className='hover:bg-secondary-foreground/30' key={char._id} value={char._id}>
                                            <div className='flex rounded-full overflow-hidden mr-2'>
                                                <img src={char?.thumbnail_url} alt={char?.name} loading="lazy" decoding="async" width={32} height={32} className='w-8 h-8' />
                                            </div>
                                            {char.name}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                }

                <div className={`relative w-full flex flex-col gap-1 items-start group/text px-1 ${element?.character ? 'max-w-[450px]' : ''} ${user.role < 20 ? 'pointer-events-none' : ''}`}>
                    <TextArea value={element?.description} placeholder='Add description...' className='italic text-secondary-foreground/70 text-[12px]/6 font-mono w-full py-1 px-3 border border-transparent group-hover/text:border-secondary-foreground/30 group-hover/text:border-dashed focus:border-secondary-foreground/30 focus:border-solid rounded-sm focus:outline-none focus:ring-0 outline-hidden bg-transparent resize-none' onChange={(e) => updateElement({description: e.target.value})} />
                    {element?.character && <TextArea value={element?.speech} placeholder='Add speech...' className='text-white text-[12px]/6 font-mono w-full py-1 px-3 border border-transparent group-hover/text:border-secondary-foreground/30 group-hover/text:border-dashed focus:border-secondary-foreground/30 focus:border-solid rounded-sm focus:outline-none focus:ring-0 outline-hidden bg-transparent resize-none' onChange={(e) => updateElement({speech: e.target.value})} />}
                </div>
                {adding && <div className='flex w-full px-1'>
                    <div className='flex w-full h-10 bg-secondary/50 rounded-lg animate-pulse flex items-center px-2 text-xs text-secondary-foreground/60'>Adding script element...</div>
                </div>}
                <UserRole role='editor'>
                    {!adding && <div className='absolute bottom-0 left-0 w-full flex justify-center opacity-0 group-hover/script:opacity-100 border-t border-dashed'>
                        <Button type='secondary' size='xs' className='-mt-3' icon={<FiPlus className='w-3 h-3' />} onClick={handleAddElement}></Button>                         
                    </div>}
                </UserRole>
            </div>
            <div className='w-[30px] flex items-center pb-6 pl-1 py-1'>
                <UserRole role='commentator'>
                    <div className='gap-1 px-1 flex opacity-0 group-hover/script:opacity-100'>
                        <Button type='secondary' size='xs' icon={<FiMessageCircle className='w-4 h-4' />} onClick={handleAddComment} /> 
                    </div>
                </UserRole>
            </div>
        </div>
    )
})

const Scene = forwardRef(function Scene({episodeId, segmentId, sceneId, onAdd, onDelete, onScriptAdd, onScriptDelete, index=0, total=0, onReorder}, ref) {
    const [scene, setScene] = useState(null)
    const [comments, setComments] = useState([])
    const [commentText, setCommentText] = useState('')
    const [commenting, setCommenting] = useState(false)
    const commentInputRef = useRef(null)
    const [deleting, setDeleting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [adding, setAdding] = useState(false)
    const [selectedElement, setSelectedElement] = useState(null)
    const [highlightedElement, setHighlightedElement] = useState(null)
    const [loading, setLoading] = useState(false)
    const [loadingComments, setLoadingComments] = useState(false)
    const [error, setError] = useState(null)
    const {show} = useShow()
    const [saveTimeout, setSaveTimeout] = useState(null)
    const {user} = useUser()
    const {openModal} = useModal()
    const scriptRefs = useRef({})

    // Read ?hc= once for comment highlighting
    const [highlightCommentId] = useState(() => {
        if (typeof window === 'undefined') return null
        return new URLSearchParams(window.location.search).get('hc')
    })

    const getData = async () => {

        try{
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${sceneId}`)
            if(!response.ok){
                throw new Error('Failed to fetch scene')
            }
            const data = await response.json()
            setScene(data)
        } catch (error) {
            console.error('Error fetching scene:', error)
            setError(error.message)
        } 
    }

    const getComments = async () => {
        try{
            const response = await apiFetch(`/api/shows/${show._id}/comments?uid=${scene?.script?.map(s => s._id).join(',') || ''}`)
            if(!response.ok){
                throw new Error('Failed to fetch comments')
            }
            const data = await response.json()
            setComments(data)
        }
        catch(error){
            console.error('Error fetching comments:', error)
            setError(error.message)
        }
    }

    const handleCommentKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleCommentSubmit()
        }else if(e.key==='Escape'){
            setCommenting(false)
            setCommentText('')
        }
    }

    const handleAddComment = async(element) => {
        setCommenting(true)
        setSelectedElement(element)
        setHighlightedElement(element._id)
    }

    const handleCommentSubmit = async () => {
        if(!selectedElement) return
        try{
            const response = await apiFetch(`/api/shows/${show._id}/comments`, {
                method: 'POST',
                body: JSON.stringify({ uid: selectedElement._id, text: commentText, element_type: 'script', episode: episodeId, segment: segmentId, scene: sceneId })
            })
            if(!response.ok){
                throw new Error('Failed to submit comment')
            }
            const data = await response.json()
            setComments([...comments, data])
        }
        catch(error){
            console.error('Error submitting comment:', error)
            setError(error.message)
        }
        finally{
            setCommenting(false)
            setCommentText('')
            setSelectedElement(null)
        }
    }

    const updateScene = async (data, debounce = true) => {
        setScene({...scene, ...data})

        if(debounce){
            debouncedSave(data)
        }else{
            handleSaveScene(data)
        }
    }

    const handleAddScript = async (index=0) => {
        try{
            setSaving(true)
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${sceneId}/script?index=${index + 1}`, {
                method: 'POST',
                body: JSON.stringify({})
            })
            if(!response.ok){
                throw new Error('Failed to add script element')
            }
            const newElement = await response.json()
            await onScriptAdd?.(newElement)
            await getData()
        } catch (error) {
            console.error('Error:', error)
        }
        finally{
            setSaving(false)
        }
    }

    const handleDeleteScript = async (script) => {
        try{
            await onScriptDelete?.(script)
            await getData()
        }
        catch(error){
            console.error('Error deleting script:', error)
        }
    }

    const handleReorderScene = async (index) => {
        await onReorder?.(scene, index)
    }

    const handleAddScene = async () => {
        try{
            setAdding(true)
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes?index=${index + 1}`, {
                method: 'POST',
                body: JSON.stringify({name: 'New Scene'})
            })
            if(!response.ok){
                throw new Error('Failed to add scene')
            }
            const newScene = await response.json()
            await onAdd?.(newScene)
        }
        catch(error){
            console.error('Error adding scene:', error)
        }
        finally{
            setAdding(false)
        }
    }

    const handleDeleteScene = async () => {
        const confirm = await openModal(<ConfirmModal title='Delete Scene' message='Are you sure you want to delete this scene?' />)
        if(!confirm) return
        try{

            setDeleting(true)
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${sceneId}`, {
                method: 'DELETE'
            })
            if(!response.ok){
                throw new Error('Failed to delete scene')
            }
            const deletedScene = await response.json()
            await onDelete?.(deletedScene)
        }
        catch(error){
            console.error('Error deleting scene:', error)
        }
        finally{
            setDeleting(false)
        }
    }

    const debouncedSave = (data) =>{
        if(saveTimeout) clearTimeout(saveTimeout)
        setSaveTimeout(setTimeout(() => handleSaveScene(data), 2000))
    }

    const handleSaveScene = async (data) => {
        try{
            setSaving(true)
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${sceneId}`, {
                method: 'PUT',
                body: JSON.stringify(data)
            })
            if(!response.ok){
                throw new Error('Failed to save scene')
            }

            await getData()
        }
        catch(error){
            console.error('Error saving scene:', error)
        }
        finally{
            setSaving(false)
        }
    }

    useEffect(() => {
        const load = async () => {
            setLoadingComments(true)
            await getComments()
            setLoadingComments(false)
        }

        if(scene?.script?.length) {
            load()
        }
    }, [scene])

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            await getData()
            setLoading(false)
        }
        load()
    }, [episodeId, segmentId, sceneId])

    useEffect(() => {
        if(commenting && commentInputRef?.current){
            setTimeout(() => {
                commentInputRef.current.focus()
            }, 100)
        }
    }, [commenting, commentInputRef])

    if(loading) {
        return (
            <div className='flex px-6 pb-6 pt-2 justify-center gap-4 relative'>
                <div className='flex w-full max-w-[900px]'>
                    <div className='flex w-full h-72 w-full max-w-2/3 bg-secondary/50 rounded-lg animate-pulse'></div>
                    <div className='flex flex-col w-full max-w-1/3 pl-10 pt-10'>
                        <div className='flex flex-col gap-2 max-w-[250px] w-full'>
                            <div className='flex w-full h-12 bg-secondary/50 rounded-lg animate-pulse'></div>
                            <div className='flex w-full h-16 bg-secondary/50 rounded-lg animate-pulse'></div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    if(error) {
        return <div className='text-red-500'>Error: {error}</div>
    }

    return (
        <div className={`flex pr-6 pl-[100px] pb-6 pt-2 justify-center text-[13px] relative text-secondary-foreground group/scene ${deleting ? 'opacity-20 animate-pulse pointer-events-none' : ''}`}>
            <div className='flex w-full max-w-[900px]'>
                <div className='w-full max-w-2/3 flex flex-col gap-1 z-10'>
                    <div className='flex flex-col gap-1 w-full border group-hover/scene:border-secondary-foreground/20 bg-card/60 border-secondary-foreground/10 rounded-lg py-2'>
                        <div className='flex px-2 gap-2'>
                            <TextArea value={scene?.name} placeholder='Add scene name...' className={`flex-1 font-mono text-primary py-1 px-2 border border-transparent hover:border-secondary-foreground/10 focus:border-secondary-foreground/30 rounded-sm focus:outline-none focus:ring-0 outline-hidden bg-transparent resize-none uppercase ${user.role < 20 ? 'pointer-events-none' : ''}`} onChange={(e) => updateScene({name: e.target.value})} />
                            <UserRole role='editor'>
                                <div className='flex items-center gap-2 opacity-0 group-hover/scene:opacity-100'>
                                    <div className='flex items-center gap-1 text-secondary-foreground/30 text-xs pointer-events-none'>
                                        {saving ? <LuLoaderCircle className='w-4 h-4 animate-spin' /> : <LuCheck className='w-4 h-4' />}
                                        <span>{saving ? 'Saving...' : 'Saved'}</span>    
                                    </div>

                                    {/* <Button type='danger' size='xs' icon={<FiTrash2 className='w-3 h-3' />} onClick={handleDeleteScene} />                        */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger>
                                            <div className='flex items-center bg-secondary text-secondary-foreground hover:bg-secondary/80 p-1 text-xs rounded-md'>
                                                <FiMoreHorizontal className='w-3 h-3' />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent>
                                            <DropdownMenuItem className='p-1 text-xs' disabled={index === 0} onClick={() => handleReorderScene(index - 1)}>
                                                <FiChevronUp className='w-4 h-4' />
                                                Move Up
                                            </DropdownMenuItem>
                                            <DropdownMenuItem className='p-1 text-xs' disabled={index === total - 1} onClick={() => handleReorderScene(index + 1)}>
                                                <FiChevronDown className='w-4 h-4' />
                                                Move Down
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem className='p-1 text-xs' onClick={handleDeleteScene}>
                                                <FiTrash2 className='w-4 h-4 text-red-500' />
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </UserRole>
                        </div>
                        {/* <TextArea value={scene?.description} placeholder='Add scene description...' className='font-mono py-1 px-2 border border-transparent hover:border-secondary-foreground/10 focus:border-secondary-foreground/30 rounded-sm focus:outline-none focus:ring-0 outline-hidden bg-transparent resize-none' onChange={(e) => updateScene({description: e.target.value})} /> */}
                        <div className='flex flex-col mt-6 w-full'>
                            {scene?.script?.map((script, scriptIndex) => (
                                <ScriptElement key={script._id} ref={(el) => scriptRefs.current[script._id] = el} data={script} index={scriptIndex} onAdd={() => handleAddScript(scriptIndex)} onDelete={handleDeleteScript} onComment={handleAddComment} highlighted={highlightedElement === script._id} saving={setSaving} />
                            ))}
                            {scene?.script?.length === 0 && 
                                <div className='flex w-full justify-center p-2'>
                                    <div className='flex items-center justify-center gap-1 border border-dashed rounded-md p-2 select-none cursor-pointer text-secondary-foreground/80 hover:text-secondary-foreground' onClick={handleAddScript} >
                                        <FiPlus className='w-4 h-4' />
                                        <span className='text-secondary-foreground/80 text-xs'>Script</span>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    <div>
                        {scene?.citations?.length > 0 && <div className='flex p-2 gap-2 flex-wrap'>
                            <span className='text-secondary-foreground/60 text-xs py-[2px]'>Sources:</span>
                            {scene.citations?.map(citation => (
                                <a key={citation._id} href={citation.url} target='_blank' rel='noopener noreferrer' className='bg-secondary px-2 py-[2px] rounded-full text-secondary-foreground text-xs cursor-pointer whitespace-nowrap hover:text-primary'>
                                    {citation.description}
                                </a>
                            ))}
                            </div>
                        }
                    </div>
                    {adding && <div className='flex w-full px-1'>
                        <div className='flex w-full h-40 justify-center items-center bg-secondary/50 rounded-lg animate-pulse flex items-center px-2 text-xs text-secondary-foreground/60'>Adding scene...</div>
                    </div>}
                </div>
                
                <div className='flex flex-col w-full max-w-1/3'>
                    <div className='flex flex-col gap-2 max-w-[300px] w-full pt-10 pl-10'>
                        {loadingComments && <div className='flex flex-col gap-2'>
                            <div className='flex w-full h-12 bg-secondary/50 rounded-lg animate-pulse'></div>
                            <div className='flex w-full h-16 bg-secondary/50 rounded-lg animate-pulse'></div>
                        </div>}
                        {comments.map((comment) => (
                            <CommentGroup key={comment._id} data={comment} onMouseEnter={() => setHighlightedElement(comment.element)} onMouseLeave={() => setHighlightedElement(null)} onDelete={getComments} highlightCommentId={highlightCommentId} />
                        ))}
                        {commenting && 
                            <div className='flex flex-col gap-1'>
                                <CommentMentionsInput inputRef={commentInputRef} onKeyDown={handleCommentKeyDown} value={commentText} onChange={setCommentText} placeholder='Add comment...' />
                                <div className='flex items-center gap-1 justify-end'>
                                    <Button type='danger-ghost' size='xs' onClick={()=>setCommenting(false)}>Cancel</Button>
                                    <Button type='confirm' size='xs' onClick={handleCommentSubmit}>Comment</Button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <UserRole role='editor'>
                {!adding && <div className='absolute bottom-0 left-0 w-full hidden justify-center group-hover/scene:flex border-t border-dashed pl-[100px] pr-6'>
                    <div className='flex w-full max-w-[900px] justify-center -mt-3'>
                        <div className='flex w-full max-w-2/3 justify-center'>
                            <Button type='secondary' size='xs' icon={<FiPlus className='w-3 h-3' />} onClick={handleAddScene} ></Button>                              
                        </div>
                        <div className='flex w-full max-w-1/3 justify-center'></div>
                    </div>
                </div>}
            </UserRole>
        </div>
    )
})

export default function ScriptPage() {
    const {show} = useShow()
    const [loading, setLoading] = useState(false)
    const [loadingStatus, setLoadingStatus] = useState('')
    const [error, setError] = useState(null)
    const [updating, setUpdating] = useState(false)
    const [prompt, setPrompt] = useState('')
    const {episodeId, segmentId} = useParams()
    const {episode, deleteScene, addScene, updateScene, fetchEpisode, deleteSegment, updateSegment, addSegment, episodeSaving, getWordCount, addScript, updateScript, deleteScript} = useEpisode()
    const {openModal} = useModal()
    const {user} = useUser()
    const [assignAssets, setAssignAssets] = useState(false)
    const { runTaskAndPoll, taskStatus, taskState } = useKagamiBridge()
    const [segment, setSegment] = useState(null)
    const [chatVisible, setChatVisible] = useState(false)

    const getData = async () => {
        try{
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}`)
            if(!response.ok){
                throw new Error('Failed to fetch segment')
            }
            const data = await response.json()
            setSegment(data)
        }
        catch(error){
            console.error('Error fetching segment:', error)
            setError(error.message)
        }
    }

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            await getData()
            setLoading(false)
        }
        load()
    }, [episodeId, segmentId])

    
    const handleChatSubmit = async () => {
        try {
            setUpdating(true)
            setPrompt('')
            await runTaskAndPoll({
                function: 'generate-segment-grok',
                showId: show._id,
                payload: {
                    episodeId: episode._id,
                    segmentId: segment._id,
                    prompt,
                    options: {assignAssets, generateVoice: true}
                }
            })
            await getData()

        } catch (error) {
            console.error('Error submitting chat:', error);

        }finally{
            setUpdating(false)
            setLoadingStatus('')
        }
    }

    const handleChatKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleChatSubmit()
        } 
    }

    const handleAddScene = async () => {
        await getData()
    }

    const handleDeleteScene = async (scene) => {
        await getData()
    }

    const handleReorderScene = async (scene, index) => {
        try{

            const scenes = segment.scenes.filter(s => s._id !== scene._id)
            scenes.splice(index, 0, scene)

            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}`, {
                method: 'PUT',
                body: JSON.stringify({scenes})
            })
            
            if(!response.ok){
                throw new Error('Failed to reorder scene')
            }
            await getData()
        }catch(error){
            console.error('Error reordering scene:', error)
        }
    }

    useEffect(() => {
       setLoadingStatus(taskStatus)
    }, [taskStatus, taskState])

    if (loading) {
        return (<Loader />)
    }

    if (error) {
        return (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <span className="text-red-400">Error: {error}</span>
        </div>
        )
    }

    return (
        <div className="flex flex-1 h-full relative">
            <ResizablePanelGroup direction="horizontal">
                <ResizablePanel  minSize={200}>
                    <div className='flex flex-col w-full h-full overflow-hidden relative'>
                        {!segment || segment?.scenes.length === 0 ? (
                                <div className='flex flex-col justify-center items-center h-full flex-1 select-none gap-4'>
                                    <div className='text-secondary-foreground/40 text-4xl font-bold'>Nothing to see here just yet</div>
                                    <div className='text-secondary-foreground/30 text-sm'>Start with a simple prompt to generate content</div>
                                </div>
                            ) : ( 
                            <div className='flex flex-col w-full overflow-y-auto pb-4'>
                                {segment?.scenes.map((scene, index) => (
                                    <Scene key={scene._id} episodeId={episode._id} segmentId={segment._id} sceneId={scene._id} index={index} total={segment.scenes.length} 
                                    onAdd={handleAddScene} 
                                    onDelete={handleDeleteScene}
                                    onReorder={handleReorderScene} />
                                ))}
                        </div>)}

                        {!chatVisible && <UserRole role='editor'>
                            <div className='pb-10 px-4 flex items-center justify-center'>
                                <div className={`flex flex-col p-4 bg-card border border-secondary gap-4 h-auto max-w-[800px] w-full rounded-lg ${loading ? 'opacity-40 pointer-events-none' : 'pointer-events-auto'}`}>
                                    <textarea
                                        value={prompt}
                                        onChange={e => {
                                            setPrompt(e.target.value);
                                            if (e.target) {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }
                                        }}
                                        onInput={e => {
                                            if (e.target) {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }
                                        }}
                                        onKeyDown={handleChatKeyDown}
                                        className='font-mono placeholder:text-secondary-foreground/30 text-secondary-foreground p-0 max-h-60 bg-transparent border-none outline-hidden text-sm/5 resize-none'
                                        rows={1}
                                        style={{ minHeight: 32 }}
                                        placeholder='Ask the AI to help you write the script...'
                                    />
                                    <div className='flex items-center gap-2 justify-between'>
                                        <div className='flex items-center gap-1'></div>
                                        <Button type='primary' icon={<FaArrowUp className='w-4 h-4' />} onClick={handleChatSubmit}/>
                                    </div>
                                </div>
                            </div>
                        </UserRole>}
                        <UserRole role='editor'>
                            <div className={`absolute top-0 right-0 p-4 cursor-pointer z-10 ${chatVisible ? 'text-primary/80 hover:text-primary': 'text-secondary-foreground/80 hover:text-secondary-foreground'}`} onClick={()=>{setChatVisible(!chatVisible)}}>
                                <LuBrainCircuit className='w-5 h-5' />
                            </div>
                        </UserRole>
                    </div>
                </ResizablePanel>
                {chatVisible && <>
                    <ResizableHandle className='outline-none ' />
                    <ResizablePanel defaultSize={400} minSize={400}>
                        <div className="flex flex-col h-full overflow-hidden">
                            <Chat scope={{ episode: episodeId, segment: segmentId }}  sideBarOpen={false} allowReferencedAssets={false} />
                        </div>
                    </ResizablePanel>
                </>}
            </ResizablePanelGroup>
            {updating && <Loader status={loadingStatus} />}
        </div>
    )
}